import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Check, Search, Send, UserPlus } from 'lucide-react';
import { partnerSessionsApi } from '../../../services/partnerSessionsApi';
import { agentsApi } from '../../../services/agentsApi';
import type { SessionPerson } from '../../../types/aai';
import { Action, ErrorNote, Field, Input, Loading, Modal, Select } from '../../../components/aa';
import { REMIND_CHOICES, ROLE_GROUPS, errorText, inGroup, matches, plural, roleLabel } from './sessionShared';
import type { RoleGroup } from './sessionShared';

/*
 * Who the session goes to, in a dialog over the draft: everyone at the client, filtered by role or
 * searched, ticked one by one or all at once. Someone who is not in the
 * company yet is added here with a role, gets their invite, and is ticked.
 */

const parseRemind = (raw: string): number | null => {
  if (!raw) return null;
  return Number(raw);
};

const ADDABLE_ROLES: { value: string; label: string }[] = [
  { value: 'AI_PARTICIPANT', label: 'Employee' },
  { value: 'DEPARTMENT_HEAD', label: 'Department head' },
  { value: 'AI_CHAMPION', label: 'AI Champion' },
];

const AddPerson: React.FC<{ clientId: string; onAdded: (email: string) => void; onCancel: () => void }> = ({ clientId, onAdded, onCancel }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('AI_PARTICIPANT');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const address = email.trim().toLowerCase();
    if (!address.includes('@')) {
      toast.info('A work email is needed.');
      return;
    }
    setBusy(true);
    try {
      await agentsApi.addWorkspaceMember(clientId, { email: address, name: name.trim() || undefined, role, send_email: true });
      toast.success(`${address} added to the company and invited.`);
      onAdded(address);
    } catch (err) {
      toast.error(errorText(err, 'Could not add the person.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-[14px] border border-line bg-canvas p-4">
      <strong className="block text-[13.5px] text-ink">Add someone new</strong>
      <small className="mb-3 block text-[12px] text-ink-muted">They join the company with this role, get their invite, and receive this session too.</small>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" /></Field>
        <Field label="Work email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" /></Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {ADDABLE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Select>
        </Field>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Action size="sm" variant="ghost" onClick={onCancel}>Cancel</Action>
        <Action size="sm" onClick={add} disabled={busy} reason="Adding">Add and select</Action>
      </div>
    </div>
  );
};

export const RecipientDrawer: React.FC<{
  clientId: string;
  title: string;
  sending: boolean;
  onClose: () => void;
  onSend: (emails: string[], remindEveryDays: number | null) => void;
}> = ({ clientId, title, sending, onClose, onSend }) => {
  const [remind, setRemind] = useState<number | null>(2);
  const [people, setPeople] = useState<SessionPerson[] | null>(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [group, setGroup] = useState<RoleGroup>('all');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await partnerSessionsApi.people(clientId);
      setPeople(r.data.people);
    } catch (err) {
      setError(errorText(err, 'Could not load the people.'));
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const everyone = useMemo(() => people || [], [people]);
  const shown = everyone.filter((p) => inGroup(p.role, group) && matches(query, p.name, p.email, p.department_name));
  const allShownPicked = shown.length > 0 && shown.every((p) => picked.has(p.email));

  const toggle = (email: string) => setPicked((now) => {
    const next = new Set(now);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    return next;
  });
  const toggleShown = () => setPicked((now) => {
    const next = new Set(now);
    shown.forEach((p) => {
      if (allShownPicked) next.delete(p.email);
      else next.add(p.email);
    });
    return next;
  });
  const added = async (email: string) => {
    setAdding(false);
    await load();
    setPicked((now) => new Set(now).add(email));
  };

  let sendLabel = 'Choose people';
  if (picked.size > 0) sendLabel = `Send to ${plural(picked.size, 'person', 'people')}`;

  let body: React.ReactNode = <Loading label="Loading the people" />;
  if (error) body = <ErrorNote message={error} />;
  if (people) {
    body = (
      <div>
        {adding && <AddPerson clientId={clientId} onAdded={added} onCancel={() => setAdding(false)} />}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {ROLE_GROUPS.map((g) => {
            const count = everyone.filter((p) => inGroup(p.role, g.key)).length;
            let tone = 'border-line bg-white text-ink-muted hover:border-ink hover:text-ink';
            if (group === g.key) tone = 'border-ink bg-ink text-white';
            return (
              <button key={g.key} type="button" onClick={() => setGroup(g.key)} className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${tone}`}>
                {g.label} <span className="opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="relative mb-3">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" aria-hidden />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, email or department" className="!pl-9" aria-label="Search people" />
        </div>
        {shown.length === 0 && (
          <div className="rounded-[14px] border border-dashed border-line px-5 py-6 text-center text-[13px] text-ink-dim">
            Nobody matches. Clear the search, pick another role, or add someone new.
          </div>
        )}
        {shown.length > 0 && (
          <div className="overflow-hidden rounded-[14px] border border-line bg-white">
            <button type="button" onClick={toggleShown} className="flex w-full items-center gap-3 border-b border-line-soft bg-canvas px-4 py-2.5 text-left text-[12.5px] font-semibold text-ink">
              <Box on={allShownPicked} />
              Select all {shown.length} shown
            </button>
            {shown.map((p) => (
              <button key={p.email} type="button" onClick={() => toggle(p.email)} className="flex w-full items-center gap-3 border-t border-line-soft px-4 py-3 text-left first:border-t-0 hover:bg-canvas">
                <Box on={picked.has(p.email)} />
                <span className="min-w-0 flex-1">
                  <b className="block truncate text-[13.5px] text-ink">{p.name || p.email}</b>
                  <small className="block truncate text-[12px] text-ink-dim">{[p.email, p.department_name].filter(Boolean).join(' · ')}</small>
                </span>
                <span className="shrink-0 rounded-full bg-canvas-inset px-2.5 py-1 text-[11px] font-semibold text-ink-muted">{roleLabel(p.role)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal
      onClose={onClose}
      size="dialog"
      context="Send session"
      badge={!adding && <Action size="sm" variant="outline" icon={<UserPlus size={14} />} onClick={() => setAdding(true)}>Add person</Action>}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[12.5px] text-ink-muted">{plural(picked.size, 'person', 'people')} selected</span>
            <label className="inline-flex items-center gap-2 text-[12.5px] text-ink-muted">
              Remind who has not answered
              <Select
                value={String(remind ?? '')}
                onChange={(e) => setRemind(parseRemind(e.target.value))}
                aria-label="Remind people who have not answered"
                className="!h-9 !w-auto !py-0 !text-[12.5px] leading-none"
              >
                {REMIND_CHOICES.map((c) => <option key={c.label} value={String(c.value ?? '')}>{c.label}</option>)}
              </Select>
            </label>
          </div>
          <div className="flex gap-2">
            <Action variant="ghost" onClick={onClose}>Back to the draft</Action>
            <Action icon={<Send size={14} />} onClick={() => onSend(Array.from(picked), remind)} disabled={picked.size === 0 || sending} reason="Sending">{sendLabel}</Action>
          </div>
        </div>
      )}
    >
      <div className="min-h-[420px]">
        <h2 className="m-0 text-[20px] font-bold leading-[26px] text-ink">{title || 'Untitled session'}</h2>
        <p className="m-0 mb-4 mt-1 text-[13px] leading-[19px] text-ink-muted">Choose who receives it. Each person gets an email and finds it in their AI Champion. Reminders stop once they answer, and after three.</p>
        {body}
      </div>
    </Modal>
  );
};

const Box: React.FC<{ on: boolean }> = ({ on }) => {
  let tone = 'border-line-strong bg-white';
  if (on) tone = 'border-ink bg-ink text-white';
  return (
    <span className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border ${tone}`} aria-hidden>
      {on && <Check size={12} strokeWidth={3} />}
    </span>
  );
};

export default RecipientDrawer;
